"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddGroup from "./components/Modals/AddGroup";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useAppServices } from "../../../../../hook/services";

export default function UIConfiguration() {
  const navigate = useNavigate();
  const AppService = useAppServices();
  const { companyId } = useParams();
  const [groups, setGroups] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDuplicateMode, setIsDuplicateMode] = useState(false);

  const handleNavigate = (groupId) => {
    navigate(`./group/${groupId}`);
  };

  const getGroups = async () => {
    setLoading(true);
    try {
      const { response } = await AppService.sidenav_customizer_custom_level.get(
        {
          query: `companyId=${companyId}`,
        }
      );
      if (response?.data) {
        setGroups(response.data);
      } else {
        setGroups([]);
      }
    } catch (error) {
      console.error("Failed to fetch groups:", error);
    } finally {
      setLoading(false);
    }
  };

  const getLocations = async () => {
    try {
      const { response } =
        await AppService.sidenav_customizer_custom_level.getLocations({
          query: `company_id=${companyId}`,
        });
      if (response && Array.isArray(response.accounts)) {
        const mappedLocations = response.accounts.map((loc) => ({
          value: loc.id,
          label: loc.name,
        }));
        setLocations(mappedLocations);
      } else {
        setLocations([]);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const saveGroups = async (groupData) => {
    const payload = {
      name: groupData.name,
      companyId: companyId,
      locations: groupData.locations.map((loc) => ({
        id: loc.value,
        name: loc.label,
      })),
      ...(isEditMode && selectedGroup?._id && { _id: selectedGroup._id }),
      ...(isDuplicateMode &&
        selectedGroup?._id && { orignalId: selectedGroup._id }),
    };

    try {
      const { response } = await AppService.sidenav_customizer_custom_level[
        isEditMode ? "update" : isDuplicateMode ? "duplicate" : "create"
      ]({
        payload,
      });

      if (response.success) {
        toast.success(
          `Group ${
            isEditMode ? "updated" : isDuplicateMode ? "duplicated" : "created"
          } successfully!`
        );
        await getGroups();
        resetGroupState();
      } else {
        toast.error(
          `Failed to ${
            isEditMode ? "update" : isDuplicateMode ? "duplicate" : "create"
          } group: ` + response.message
        );
      }
    } catch (error) {
      console.error(
        `Error ${
          isEditMode ? "updating" : isDuplicateMode ? "duplicating" : "creating"
        } group:`,
        error
      );
      toast.error("Something went wrong.");
    }
  };

  const deleteGroup = async (groupId) => {
    const result = await Swal.fire({
      title: "Delete this group?",
      text: "This will permanently delete the group and its customization. This action cannot be undone.",
      iconHtml:
        '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
      showCancelButton: true,
      confirmButtonText: "Delete group",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "lg-swal-popup",
        icon: "lg-swal-icon",
        title: "lg-swal-title",
        htmlContainer: "lg-swal-text",
        actions: "lg-swal-actions",
        confirmButton: "lg-swal-btn lg-swal-btn--danger",
        cancelButton: "lg-swal-btn lg-swal-btn--ghost",
      },
    });
    if (result.isConfirmed) {
      setLoading(true);

      try {
        const { response } =
          await AppService.sidenav_customizer_custom_level.delete({
            query: `_id=${groupId}`,
          });
        if (response.success) {
          toast.success("Group deleted successfully!");
          await getGroups();
        } else {
          toast.error("Failed to delete group: " + response.message);
        }
      } catch (error) {
        console.error("Error deleting group:", error);
        toast.error("Failed to delete group.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDuplicateGroup = (group) => {
    setSelectedGroup(group);
    setIsEditMode(false);
    setIsDuplicateMode(true);
  };

  const handleEditGroup = (group) => {
    setSelectedGroup(group);
    setIsEditMode(true);
  };

  const resetGroupState = () => {
    setSelectedGroup(null);
    setIsEditMode(false);
    setIsDuplicateMode(false);
  };

  const usedLocationValues = groups
    .filter((group) => (isEditMode ? group._id !== selectedGroup?._id : true))
    .flatMap((group) => group.locations)
    .map((loc) => loc.id);

  const availableLocations = locations.filter(
    (loc) => !usedLocationValues.includes(loc.value)
  );

  useEffect(() => {
    getGroups();
    getLocations();
  }, []);

  const renderLocationChips = (groupLocations = []) => {
    const visible = groupLocations.slice(0, 3);
    const overflow = groupLocations.length - visible.length;
    return (
      <div className="lg-chips d-flex flex-wrap gap-1 mt-2">
        {visible.map((loc, i) => (
          <span key={i} className="lg-chip">
            {loc.name}
          </span>
        ))}
        {overflow > 0 && (
          <span
            className="lg-chip lg-chip--more"
            title={groupLocations.map((l) => l.name).join(", ")}
          >
            +{overflow} more
          </span>
        )}
      </div>
    );
  };

  return (
    <div
      className="min-vh-100 bg-white text-dark"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="container py-5" style={{ maxWidth: 960 }}>
        {/* Back */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="lg-back-btn d-inline-flex align-items-center mb-4 bg-transparent border-0 p-0 text-secondary"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Back</span>
        </button>

        {/* Header */}
        <header className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <h1 className="fw-semibold mb-2" style={{ fontSize: "1.625rem", letterSpacing: "-0.01em" }}>
              Location groups
            </h1>
            <p className="text-secondary mb-0" style={{ fontSize: "0.9375rem", lineHeight: 1.55 }}>
              Customize the user interface for different location groups within your organization.
            </p>
          </div>
          <div className="lg-add-slot">
            <AddGroup
              ghl_locations={availableLocations}
              saveGroups={saveGroups}
              selectedGroup={selectedGroup}
              isEditMode={isEditMode}
              isDuplicateMode={isDuplicateMode}
              resetGroupState={resetGroupState}
            />
          </div>
        </header>

        {/* Content */}
        <div className="d-flex flex-column gap-2">
          {loading ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5 text-secondary">
              <div className="spinner-border spinner-border-sm mb-2" role="status" aria-hidden="true" />
              <span style={{ fontSize: "0.8125rem" }}>Loading groups…</span>
            </div>
          ) : Array.isArray(groups) && groups.length > 0 ? (
            groups.map((group, index) => (
              <article key={index} className="lg-card">
                <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <h2 className="lg-card-title mb-0">{group.name}</h2>
                      <span className="lg-count-badge">
                        {group.locations?.length || 0}{" "}
                        {(group.locations?.length || 0) === 1 ? "location" : "locations"}
                      </span>
                    </div>
                    {group.locations?.length > 0 && renderLocationChips(group.locations)}
                  </div>

                  <div className="lg-actions d-flex align-items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      className="lg-btn lg-btn--ghost"
                      onClick={() => handleDuplicateGroup(group)}
                      title="Duplicate group"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      Duplicate
                    </button>
                    <button
                      type="button"
                      className="lg-icon-btn"
                      onClick={() => handleEditGroup(group)}
                      title="Edit group"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="lg-icon-btn lg-icon-btn--danger"
                      onClick={() => deleteGroup(group._id)}
                      title="Delete group"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="lg-btn lg-btn--primary ms-1"
                      onClick={() => handleNavigate(group._id)}
                    >
                      Customize
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="ms-1">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  </div>
                </div>
              </article>
            ))
          ) : (
            <div className="lg-empty d-flex flex-column align-items-center text-center py-5">
              <div className="lg-empty-icon d-flex align-items-center justify-content-center mb-3">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="fw-semibold text-dark mb-1" style={{ fontSize: "0.9375rem" }}>
                No location groups yet
              </div>
              <div className="text-secondary" style={{ fontSize: "0.8125rem" }}>
                Use the "Add new group" button to create your first group.
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .lg-back-btn { transition: color 0.15s ease; }
        .lg-back-btn:hover { color: #1f2937 !important; }
        .lg-back-btn:hover svg { transform: translateX(-2px); }
        .lg-back-btn svg { transition: transform 0.15s ease; }

        .lg-card {
          background: #fff;
          border: 1px solid #ececec;
          border-radius: 12px;
          padding: 16px 18px;
          transition: border-color 0.15s ease, box-shadow 0.2s ease;
        }
        .lg-card:hover {
          border-color: #dcdcdc;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04), 0 4px 12px rgba(17, 24, 39, 0.04);
        }
        .lg-card-title {
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
          letter-spacing: -0.005em;
        }

        .lg-count-badge {
          display: inline-flex;
          align-items: center;
          padding: 2px 8px;
          font-size: 0.6875rem;
          font-weight: 500;
          color: #4b5563;
          background: #f3f4f6;
          border-radius: 999px;
          letter-spacing: 0.01em;
        }

        .lg-chips { max-width: 540px; }
        .lg-chip {
          display: inline-flex;
          align-items: center;
          padding: 3px 9px;
          font-size: 0.75rem;
          color: #374151;
          background: #fafafa;
          border: 1px solid #ececec;
          border-radius: 6px;
          line-height: 1.4;
        }
        .lg-chip--more {
          color: #4f46e5;
          background: #eef2ff;
          border-color: #e0e7ff;
          font-weight: 500;
          cursor: default;
        }

        .lg-actions { gap: 6px !important; }

        .lg-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.005em;
          padding: 8px 14px;
          border-radius: 8px;
          border: 1px solid transparent;
          line-height: 1;
          cursor: pointer;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
          font-family: inherit;
        }
        .lg-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }

        .lg-btn--ghost {
          background: #fff;
          border-color: #e5e7eb;
          color: #1f2937;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
        }
        .lg-btn--ghost:hover {
          background: #fafafa;
          border-color: #d1d5db;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 6px 14px rgba(17, 24, 39, 0.06);
        }
        .lg-btn--ghost:active { transform: translateY(0); }

        .lg-btn--primary {
          background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.04);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(79, 70, 229, 0.22),
            0 4px 10px rgba(79, 70, 229, 0.2);
        }
        .lg-btn--primary:hover {
          background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(67, 56, 202, 0.28),
            0 8px 18px rgba(79, 70, 229, 0.3);
        }
        .lg-btn--primary:hover svg { transform: translateX(2px); }
        .lg-btn--primary:active { transform: translateY(0); }
        .lg-btn--primary svg { transition: transform 0.15s ease; }

        .lg-icon-btn {
          width: 32px;
          height: 32px;
          border: 1px solid #e5e7eb;
          background: #fff;
          color: #4b5563;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
          transition: transform 0.15s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.2s ease;
        }
        .lg-icon-btn:hover {
          background: #fafafa;
          border-color: #d1d5db;
          color: #1f2937;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 4px 10px rgba(17, 24, 39, 0.06);
        }
        .lg-icon-btn:active { transform: translateY(0); }
        .lg-icon-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }
        .lg-icon-btn--danger:hover {
          color: #b91c1c;
          background: #fef2f2;
          border-color: #fecaca;
          box-shadow: 0 2px 6px rgba(185, 28, 28, 0.1);
        }

        .lg-empty {
          border: 1px dashed #e5e5e5;
          border-radius: 12px;
          background: #fafafa;
        }
        .lg-empty-icon {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: #fff;
          border: 1px solid #ececec;
          color: #9ca3af;
        }

        /* Delete-confirmation dialog (SweetAlert2 customization) */
        .lg-swal-popup {
          border-radius: 14px !important;
          border: 1px solid #ececec;
          font-family: 'Inter', sans-serif !important;
          padding: 6px 4px 14px !important;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18) !important;
          width: 420px !important;
        }
        .swal2-icon.lg-swal-icon {
          width: 56px !important;
          height: 56px !important;
          border-width: 1px !important;
          border-color: #fecaca !important;
          background: #fef2f2 !important;
          color: #dc2626 !important;
          margin: 18px auto 6px !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
        }
        .swal2-icon.lg-swal-icon .swal2-icon-content {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0;
        }
        .swal2-icon.lg-swal-icon .swal2-icon-content svg {
          stroke: #dc2626;
        }
        .lg-swal-title {
          font-size: 1.0625rem !important;
          font-weight: 600 !important;
          color: #1f2937 !important;
          letter-spacing: -0.005em;
          padding: 4px 16px 0 !important;
          margin: 0 !important;
        }
        .lg-swal-text {
          font-size: 0.875rem !important;
          color: #6b7280 !important;
          line-height: 1.5 !important;
          padding: 8px 24px 0 !important;
          margin: 0 !important;
        }
        .lg-swal-actions {
          margin: 22px 16px 4px !important;
          gap: 8px !important;
          justify-content: flex-end !important;
        }
        .lg-swal-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8125rem !important;
          font-weight: 600 !important;
          letter-spacing: 0.005em;
          padding: 9px 16px !important;
          border-radius: 9px !important;
          border: 1px solid transparent !important;
          line-height: 1;
          cursor: pointer;
          font-family: inherit;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
          margin: 0 !important;
        }
        .lg-swal-btn:focus {
          outline: none !important;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.18) !important;
        }
        .lg-swal-btn--ghost {
          background: #fff !important;
          border-color: #e5e7eb !important;
          color: #1f2937 !important;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04) !important;
        }
        .lg-swal-btn--ghost:hover {
          background: #fafafa !important;
          border-color: #d1d5db !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 6px 14px rgba(17, 24, 39, 0.06) !important;
        }
        .lg-swal-btn--ghost:active { transform: translateY(0); }
        .lg-swal-btn--danger {
          background: linear-gradient(180deg, #ef4444 0%, #dc2626 100%) !important;
          color: #fff !important;
          border-color: rgba(255, 255, 255, 0.04) !important;
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(185, 28, 28, 0.22),
            0 4px 10px rgba(220, 38, 38, 0.2) !important;
        }
        .lg-swal-btn--danger:hover {
          background: linear-gradient(180deg, #dc2626 0%, #b91c1c 100%) !important;
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(153, 27, 27, 0.28),
            0 8px 18px rgba(220, 38, 38, 0.3) !important;
        }
        .lg-swal-btn--danger:active { transform: translateY(0); }
        .swal2-container.swal2-backdrop-show {
          background: rgba(15, 23, 42, 0.45) !important;
          backdrop-filter: blur(2px);
        }
      `}</style>
    </div>
  );
}
